import { Button, HStack, List, Text, VStack } from "@chakra-ui/react"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import { useEffect, useRef } from "react"
import { PublishCardTextButton } from "./ContentPublishCard"

type PublishConfirmationAction = "customize" | "revert" | "discard"

const ContentPublishConfirmation = ({
  action,
  cohortName,
  showResponseWarning,
  busy,
  onConfirm,
  onCancel
}: {
  action: PublishConfirmationAction
  cohortName?: string
  showResponseWarning?: boolean | null
  busy?: boolean
  onConfirm: () => void
  onCancel: () => void
}) => {
  const { tc, Trans } = useTranslate()
  const confirmRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (!busy) confirmRef.current?.focus()
  }, [busy])
  const title =
    action === "customize"
      ? tc("Customize content for {{cohort}}?", { cohort: cohortName })
      : action === "revert"
      ? tc("Revert to source version?")
      : tc("Discard unpublished changes?")
  const confirmLabel =
    action === "customize"
      ? tc("Yes, customize")
      : action === "revert"
      ? tc("Yes, revert to source version")
      : tc("Yes, discard changes")

  return (
    <VStack align="center" gap={4} w="336px" maxW="100%">
      <Text as="h3" fontWeight="700" m={0} w="full" textAlign="center">
        {title}
      </Text>
      <VStack
        role={
          action === "customize" && showResponseWarning ? "note" : undefined
        }
        lineHeight="1.4"
        w="full"
        textAlign="left"
        align="stretch"
        gap={2}
      >
        {action === "customize" ? (
          showResponseWarning ? (
            <>
              <Text>
                <Trans
                  i18nKey="Customizing creates a unique copy for members of the <strong>{{cohort}}</strong> Cohort:"
                  values={{ cohort: cohortName }}
                  components={{ strong: <strong /> }}
                  tOptions={{ keySeparator: false }}
                />
              </Text>
              <List.Root ps={5}>
                <List.Item>
                  <Trans i18nKey="blocks-customize-responses-warning">
                    Responses left on the Source version will no longer be
                    visible in the Cohort, but are saved with the Source
                    version.
                  </Trans>
                </List.Item>
                <List.Item>
                  {tc(
                    "Blocks content will no longer receive updates from the Source."
                  )}
                </List.Item>
              </List.Root>
              <Text>
                {tc("You can revert back to the Source version anytime.")}
              </Text>
            </>
          ) : (
            <Text>
              <Trans
                i18nKey="Customizing creates a unique copy for members of the <strong>{{cohort}}</strong> Cohort. It will no longer be updated from the shared Source content, but you can revert back to the Source version anytime."
                values={{ cohort: cohortName }}
                components={{ strong: <strong /> }}
                tOptions={{ keySeparator: false }}
              />
            </Text>
          )
        ) : action === "revert" ? (
          <Text>
            <Trans
              i18nKey="Reverting the Blocks content will replace any unique Block updates made in this <strong>{{cohort}}</strong> Cohort with the shared Source content."
              values={{ cohort: cohortName }}
              components={{ strong: <strong /> }}
              tOptions={{ keySeparator: false }}
            />
          </Text>
        ) : (
          <Text>
            {tc(
              "Replace this document’s draft with its last published content. This will discard unpublished changes.",
              { keySeparator: false }
            )}
          </Text>
        )}
        {showResponseWarning && action === "revert" && (
          <Text role="note" lineHeight="1.4" w="full" textAlign="left">
            <Trans i18nKey="blocks-revert-responses-warning">
              Additionally, responses left in this custom Cohort content will no
              longer be visible, but will be stored.
            </Trans>
          </Text>
        )}
      </VStack>
      <HStack gap={3} mt={1} justify="center" flexWrap="wrap">
        <Button
          ref={confirmRef}
          colorPalette="brand"
          loading={busy}
          disabled={busy}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
        <PublishCardTextButton disabled={busy} onClick={onCancel}>
          {tc("No, nevermind")}
        </PublishCardTextButton>
      </HStack>
    </VStack>
  )
}

export default ContentPublishConfirmation
